// Modul 40 B5 — das Board: alle offenen Bestellungen als Karten.
//
// Das Board ersetzt die Tabelle NUR im Panel „Bestellungen" (offene Vorgaenge). Die Panels
// „Abgeschlossen" (mit Summenzeile) und „Storniert" bleiben Tabellen: dort wird nichts mehr
// besorgt, dort wird nachgeschlagen — und eine Tabelle liest sich zum Nachschlagen besser.
//
// Welche Ansicht zuletzt gewaehlt war, merkt sich der Browser unter `einkauf.ansicht`.
// Dieser Schluessel ist zugleich der Deploy-Marker von Teil B.
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { PurchaseOrderListRow } from "@/api/purchase";
import { BestellKarte } from "./BestellKarte";

export type EinkaufAnsicht = "board" | "tabelle";

/** Deploy-Marker Teil B — der String muss im gebauten Bundle auffindbar bleiben. */
export const ANSICHT_KEY = "einkauf.ansicht";

/**
 * Die gemerkte Ansicht.
 *
 * Alles ausser dem woertlichen `"tabelle"` fuehrt auf das Board: ein alter oder von Hand
 * verstellter Wert soll die Seite nicht in einen Zustand bringen, den es nicht gibt.
 * `localStorage` kann werfen (privater Modus, gesperrte Speicherung) — dann eben Board.
 */
export function leseAnsicht(): EinkaufAnsicht {
  try {
    return localStorage.getItem(ANSICHT_KEY) === "tabelle" ? "tabelle" : "board";
  } catch {
    return "board";
  }
}

export function merkeAnsicht(a: EinkaufAnsicht): void {
  try {
    localStorage.setItem(ANSICHT_KEY, a);
  } catch {
    /* Kein Gedaechtnis ist besser als eine Fehlermeldung fuer eine Nebensaechlichkeit. */
  }
}

export function EinkaufBoard(p: {
  rows: PurchaseOrderListRow[];
  onOpen: (id: number) => void;
  onCsv: (id: number) => void;
  darfPreise: boolean;
}) {
  // Die offenen Karten stehen als Menge von Ids — nicht als Index. Die Liste sortiert sich
  // beim Neuladen um, ein Index waere danach eine andere Bestellung.
  const [offene, setOffene] = useState<Set<number>>(new Set());

  if (p.rows.length === 0) {
    return <div className="py-6 text-center text-sm text-ink-2">Keine offenen Bestellungen.</div>;
  }

  const alleOffen = p.rows.every((r) => offene.has(r.id));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-ink-2">
        <span>{p.rows.length} {p.rows.length === 1 ? "Bestellung" : "Bestellungen"}</span>
        <Button
          size="sm" variant="ghost" className="h-7 px-2 text-xs"
          // Vereinigung bzw. Differenz statt Neuaufbau: der Knopf spricht ueber die
          // GEZEIGTEN Zeilen. Sobald diese Liste einmal gefiltert wird, wuerde ein
          // `new Set(...)` den Zustand der gerade unsichtbaren Karten mitloeschen
          // (Befund Reviewer 2) — heute unsichtbar, morgen ein Fehlerbericht.
          onClick={() => setOffene((s) => {
            const neu = new Set(s);
            for (const r of p.rows) { if (alleOffen) neu.delete(r.id); else neu.add(r.id); }
            return neu;
          })}
        >
          {alleOffen ? "Alle zuklappen" : "Alle aufklappen"}
        </Button>
      </div>

      <div className="space-y-2">
        {p.rows.map((row) => (
          <BestellKarte
            key={row.id}
            row={row}
            offen={offene.has(row.id)}
            onToggle={() => setOffene((s) => {
              const neu = new Set(s);
              if (neu.has(row.id)) neu.delete(row.id); else neu.add(row.id);
              return neu;
            })}
            onOpen={p.onOpen}
            onCsv={p.onCsv}
            darfPreise={p.darfPreise}
          />
        ))}
      </div>
    </div>
  );
}
